import {
  AssetModel,
  PortableAssetCategory,
  PortableAssetClass,
  PortableAssetPropertiesValues,
} from '../../../../../util/enums';
import { DEDICATED_PROPERTY_KEYS } from '../../../../Assets/helpers';
import {
  asProductionCapacityUnitOrUndefined,
  convertProductionCapacityValue,
  DEFAULT_PRODUCTION_CAPACITY_UNIT,
  displayProductionCapacityUnitByUnit,
  formatProductionCapacityNumber,
} from '../../../../../util/productionCapacityConfig';

export { EMISSIONS_PROPERTY_KEYS } from '../../../../Assets/helpers';

export const allowedEmissionRates = [
  { name: 'Cubic metres per second', unit: 'm^3/s', displayUnit: 'm³/s' },
  { name: 'Cubic feet per minute', unit: 'cfm', displayUnit: 'cfm' },
] as const;

export const allowedEmissionRateUnits = allowedEmissionRates.map(rate => rate.unit);
export type EmissionRateUnit = (typeof allowedEmissionRateUnits)[number];
export const displayEmissionRateUnitByUnit: Record<EmissionRateUnit, string> = {
  'm^3/s': 'm³/s',
  cfm: 'cfm',
};

export const DEFAULT_EMISSION_RATE_UNIT: EmissionRateUnit = 'm^3/s';
export const asEmissionRateUnitOrUndefined = (value?: string): EmissionRateUnit | undefined =>
  allowedEmissionRateUnits.find(u => u === value);
export const asEmissionRateUnitOrDefault = (value?: string): EmissionRateUnit =>
  asEmissionRateUnitOrUndefined(value) ?? DEFAULT_EMISSION_RATE_UNIT;

const emissionNumberFormatter = Intl.NumberFormat(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
export const formatEmissionNumber = (num: number) => emissionNumberFormatter.format(num);

export const portableAssetCategoryLabel: Record<PortableAssetCategory, string> = {
  personnel: 'Personnel',
  device: 'Device',
  'light vehicle': 'LV',
  truck: 'Truck',
  bogger: 'Loader',
  IT: 'ITC',
  drill: 'Drill',
  'other heavy vehicle': 'Other heavy vehicle',
  'untracked asset': 'Untracked asset',
  tool: 'Tool',
  integration: 'Integration',
} as const;

export const portableAssetPropertyLabel: Record<PortableAssetPropertiesValues, string> = {
  inactive: 'Inactive',
  visibility: 'Visibility',
  emissions_rate: 'Emissions Rate',
  emissions_unit: 'Emissions Unit',
  truckingFactor: 'Trucking Factor',
  truckingFactor_unit: 'Trucking Factor Unit',
  production_capacity: 'Capacity',
  production_capacity_unit: 'Capacity Unit',
} as const;

export const computeMetadataRows = (
  beacon: {
    label?: string;
    id?: string;
    properties?: Record<string, string>;
    derived?: { category: PortableAssetCategory; class: PortableAssetClass };
    model?: AssetModel;
  },
  productionCapacityProjectUnit?: string,
): { index: number; property: string; value: string }[] => {
  const category = beacon?.derived?.category;
  const emissionRate = beacon?.properties?.emissions_rate;
  const emissionUnit = beacon?.properties?.emissions_unit;
  const emissionRateNumber = Number(emissionRate);

  const productionCapacity = beacon?.properties?.production_capacity;
  const productionCapacityUnit = beacon?.properties?.production_capacity_unit;
  // Mirror the AssetsForm Capacity field: always convert the stored value to the project
  // (service-config) unit, defaulting to the standard unit when none is configured.
  const displayCapacityUnit =
    asProductionCapacityUnitOrUndefined(productionCapacityProjectUnit) ??
    DEFAULT_PRODUCTION_CAPACITY_UNIT;
  const displayCapacityNumber = Number(
    convertProductionCapacityValue(productionCapacity, productionCapacityUnit, displayCapacityUnit),
  );

  const fixedRows: { property: string; value: string }[] = [
    beacon?.derived?.class ? { property: 'Class', value: beacon.derived.class } : undefined,
    category ? { property: 'Category', value: portableAssetCategoryLabel[category] } : undefined,
    beacon?.model ? { property: 'Model', value: beacon.model.replaceAll('_', ' ') } : undefined,
    emissionRate && Number.isFinite(emissionRateNumber)
      ? {
          property: 'Emissions Rate',
          value: `${formatEmissionNumber(emissionRateNumber)}\u00A0${
            displayEmissionRateUnitByUnit[asEmissionRateUnitOrDefault(emissionUnit)]
          }`,
        }
      : undefined,
    productionCapacity && Number.isFinite(displayCapacityNumber)
      ? {
          property: 'Capacity',
          value: `${formatProductionCapacityNumber(displayCapacityNumber)}\u00A0${
            displayProductionCapacityUnitByUnit[displayCapacityUnit]
          }`,
        }
      : undefined,
  ].filter((row): row is { property: string; value: string } => row !== undefined);

  const customRows = Object.entries(beacon?.properties ?? {})
    .filter(([key, value]) => !DEDICATED_PROPERTY_KEYS.has(key) && !!value)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([property, value]) => ({ property, value }));

  return [...fixedRows, ...customRows].map((row, index) => ({ ...row, index }));
};
